import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Bell, EnvelopeSimple, Moon, Sun, SignOut, ShareNetwork, Receipt } from '@phosphor-icons/react';

const LOGO_DARK = "https://customer-assets.emergentagent.com/job_da191abd-9276-45a7-bd48-1757c1f4e471/artifacts/p5urxzpf_diametor_logo_black__1_-removebg-preview.png";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_debt-cycle/artifacts/gzyrfqiy_diametor_logo_white-removebg-preview.png";
const SHARE_TEXT = "Check out Diametor - The AI-driven engine that clears cyclic debts and makes invisible TOR cycles happen across millions simultaneously.";
const SHARE_URL = "https://diametor.com";

export default function Navbar({ unreadCount, onNotificationClick }) {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Diametor - Flow Through Me',
          text: SHARE_TEXT,
          url: SHARE_URL,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          navigator.clipboard.writeText(`${SHARE_TEXT}\n${SHARE_URL}`);
        }
      }
    } else {
      navigator.clipboard.writeText(`${SHARE_TEXT}\n${SHARE_URL}`);
    }
  };

  return (
    <nav 
      className={`sticky top-0 z-40 backdrop-blur-xl border-b ${
        isDark 
          ? 'bg-zinc-950/80 border-zinc-800' 
          : 'bg-white/80 border-zinc-200'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <img 
            src={isDark ? LOGO_DARK : LOGO_LIGHT} 
            alt="Diametor" 
            className="h-7"
            data-testid="navbar-logo"
          />

          {/* Actions */}
          <div className="flex items-center gap-1">
            {/* Transactions */}
            <button
              onClick={() => navigate('/transactions')}
              className={`p-2 rounded-sm transition-colors ${isDark ? 'hover:bg-zinc-800 text-zinc-400 hover:text-yellow-400' : 'hover:bg-zinc-100 text-zinc-600 hover:text-yellow-500'}`}
              data-testid="transactions-button"
              title="Transaction History"
            >
              <Receipt size={20} weight="bold" />
            </button>

            {/* Notifications */}
            <button
              onClick={onNotificationClick}
              className={`relative p-2 rounded-sm transition-colors ${isDark ? 'hover:bg-zinc-800 text-zinc-400 hover:text-zinc-50' : 'hover:bg-zinc-100 text-zinc-600 hover:text-zinc-950'}`}
              data-testid="notification-button"
            >
              <Bell size={20} weight="bold" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-yellow-400 text-zinc-950 text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
                  {unreadCount}
                </span>
              )}
            </button>

            {/* Contact */}
            <a
              href="mailto:support@diametor.com"
              className={`p-2 rounded-sm transition-colors ${isDark ? 'hover:bg-zinc-800 text-zinc-400 hover:text-zinc-50' : 'hover:bg-zinc-100 text-zinc-600 hover:text-zinc-950'}`}
              data-testid="navbar-contact"
            >
              <EnvelopeSimple size={20} weight="bold" />
            </a>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-sm transition-colors ${isDark ? 'hover:bg-zinc-800 text-zinc-400 hover:text-yellow-400' : 'hover:bg-zinc-100 text-zinc-600 hover:text-yellow-500'}`}
              data-testid="theme-toggle"
            >
              {isDark ? <Sun size={20} weight="bold" /> : <Moon size={20} weight="bold" />}
            </button>

            {/* Share */}
            <button
              onClick={handleShare}
              className={`p-2 rounded-sm transition-colors ${isDark ? 'hover:bg-zinc-800 text-zinc-400 hover:text-yellow-400' : 'hover:bg-zinc-100 text-zinc-600 hover:text-yellow-500'}`}
              data-testid="share-button"
              title="Share Diametor"
            >
              <ShareNetwork size={20} weight="bold" />
            </button>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className={`p-2 rounded-sm transition-colors ${isDark ? 'hover:bg-zinc-800 text-zinc-400 hover:text-red-400' : 'hover:bg-zinc-100 text-zinc-600 hover:text-red-500'}`}
              data-testid="logout-button"
            >
              <SignOut size={20} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
